import './sign-up-form.styles.scss';
import { useState, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, Box } from '@mui/material';
import RoundedBox from '../rounded-box/rounded-box.component';
import FormField from '../form-field/form-field.component';
import Loader from '../loader/loader.component';
import { registerUser } from '../../service/authentication.service';
import { UserContext } from '../../context/user.context';
import { CheckoutContext } from '../../context/checkout.context';
import { cleanPhoneMask } from '../../utils/string.utils';

const defaultFormFields = {
    firstName: '',
    lastName: '',
    phone: '',
    email: '',
    emailConfirmation: '',
    password: '',
    passwordConfirmation: '',
};

const defaultErrors = {
    firstName: '',
    lastName: '',
    phone: '',
    email: '',
    emailConfirmation: '',
    password: '',
    passwordConfirmation: '',
};

const SignUpForm = () => {
    const navigate = useNavigate();
    const [formFields, setFormFields] = useState(defaultFormFields);
    const [errors, setErrors] = useState(defaultErrors);
    const [isLoading, setIsLoading] = useState(false);

    const { setCurrentUser } = useContext(UserContext);
    const { firstName, lastName, phone, email, emailConfirmation, password, passwordConfirmation } = formFields;
    const { selectedInsurance } = useContext(CheckoutContext);

    const handleChange = (event) => {
        const { name, value } = event.target;
        setFormFields({ ...formFields, [name]: value});
    };

    const isFormValid = () => {
        let isValid = true;
        let newErrors = defaultErrors;
        const newPhone = cleanPhoneMask(phone);

        if (!/^[A-Za-zÀ-ÖØ-öø-ÿ\s]+$/.test(firstName)) {
            newErrors = {
                ...newErrors,
                firstName: "Apenas letras são permitidas"
            };
            
            isValid = false;
        }

        if (!/^[A-Za-zÀ-ÖØ-öø-ÿ\s]+$/.test(lastName)) {
            newErrors = {
                ...newErrors,
                lastName: "Apenas letras são permitidas"
            };
            
            isValid = false;
        }

        if (newPhone.length < 11) {
            newErrors = {
                ...newErrors,
                phone: "Número inválido"
            };
            
            isValid = false;
        }

        if (email != emailConfirmation) {
            newErrors = {
                ...newErrors,
                email: "E-mails devem ser iguais",
                emailConfirmation: "E-mails devem ser iguais"
            };
            
            isValid = false;
        }

        if (password != passwordConfirmation) {
            newErrors = {
                ...newErrors,
                password: "Senhas devem ser iguais",
                passwordConfirmation: "Senhas devem ser iguais"
            };
            
            isValid = false;
        }

        if (password.length < 6) {
            newErrors = {
                ...newErrors,
                password: "Senha: mínimo 6 caracteres"
            };
            
            isValid = false;
        }

        setErrors(newErrors);
        return isValid;
    }

    const handleSubmit = (event) => {
        event.preventDefault();

        if (isFormValid()) {
            setIsLoading(true);
            registerUser(formFields).then(user => {
                setIsLoading(false);
                if (user instanceof Object) {
                    setCurrentUser(user);
        
                    if (!!selectedInsurance?.insuranceName) {
                        navigate('/checkout')
                    } else {
                        navigate('/');
                    }
                } else {
                    setErrors({
                        ...defaultErrors,
                        email: user
                    });
                }
            }).catch(error => {
                setIsLoading(false);
                console.error(error);
                alert('Ocorreu um erro com o seu registro.');
            })
        }
    };

    const getErrorMessage = (fieldName) => {
        return !!errors ? errors[fieldName] : null;
    };

    return (
        <div>
            {
                isLoading ? (
                    <Loader/>
                ) : (
                    <RoundedBox className='sign-up-form-container'>
                        <form className='sign-up-form' onSubmit={handleSubmit}>
                            <label className='title'>Crie a sua conta:</label>
                            <Box
                                sx={{
                                    display: 'grid',
                                    gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
                                    columnGap: 2,
                                    rowGap: 3,
                                }}
                            >
                                <FormField
                                    darkBackground
                                    label='Nome'
                                    name='firstName'
                                    type='text'
                                    required
                                    value={firstName}
                                    onChange={handleChange}
                                    errorMessage={getErrorMessage('firstName')}/>
                                <FormField
                                    darkBackground
                                    label='Sobrenome'
                                    name='lastName'
                                    type='text'
                                    required
                                    value={lastName}
                                    onChange={handleChange}
                                    errorMessage={getErrorMessage('lastName')}/>
                                <Box sx={{ gridColumn: '1 / -1' }}>
                                    <FormField
                                        darkBackground
                                        label='Celular'
                                        name='phone'
                                        type='text'
                                        required
                                        value={phone}
                                        mask='(99) 99999-9999'
                                        onChange={handleChange}
                                        errorMessage={getErrorMessage('phone')}/>
                                </Box>
                                <FormField
                                    darkBackground
                                    label='E-mail'
                                    name='email'
                                    type='email'
                                    required
                                    value={email}
                                    onChange={handleChange}
                                    errorMessage={getErrorMessage('email')}/>
                                <FormField
                                    darkBackground
                                    label='Confirmar e-mail'
                                    autoComplete="off"
                                    name='emailConfirmation'
                                    type='email'
                                    required
                                    value={emailConfirmation}
                                    onChange={handleChange}
                                    errorMessage={getErrorMessage('emailConfirmation')}/>
                                <FormField
                                    darkBackground
                                    label='Senha'
                                    name='password'
                                    type='password'
                                    required
                                    value={password}
                                    onChange={handleChange}
                                    errorMessage={getErrorMessage('password')}/>
                                <FormField
                                    darkBackground
                                    label='Confirmar senha'
                                    name='passwordConfirmation'
                                    type='password'
                                    required
                                    value={passwordConfirmation}
                                    onChange={handleChange}
                                    errorMessage={getErrorMessage('passwordConfirmation')}/>
                            </Box>
                            <div className='footer'>
                                <Button type='submit' className='sign-up button' variant='contained'>Cadastrar</Button>
                            </div>
                            <div className='sign-in-prompt'>
                                Já tem uma conta?{' '}
                                <span className='sign-in-link' onClick={() => navigate('/authentication')}>
                                    Entrar
                                </span>
                            </div>
                        </form>
                    </RoundedBox>
                )
            }
        </div>
    )
}

export default SignUpForm;